import { resolveIncidentSinkAlertSeverity } from '@/shared/observability/incident-sink-alert';
import type {
    BrowserDispatchedIncident,
    IncidentSender,
    IncidentSenderResult,
} from '@/shared/observability/incident-dispatch';

export const DEFAULT_BROWSER_INCIDENT_RELAY_PATH = '/api/observability/incidents/browser';

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
    return Object.prototype.toString.call(value) === '[object Object]';
};

const readOptionalString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
};

const pruneUndefined = (value: Record<string, unknown>): Record<string, unknown> => {
    return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null));
};

const buildBrowserRelayDedupeKey = (incident: BrowserDispatchedIncident): string | null => {
    const metadataRecord = isObjectRecord(incident.metadata) ? incident.metadata : null;
    const prefix = metadataRecord?.kind === 'client_runtime_boundary' ? 'client_runtime' : 'browser_incident';
    const baseKey =
        readOptionalString(incident.fingerprint) ??
        readOptionalString(incident.requestId) ??
        readOptionalString(metadataRecord?.failureId);

    return baseKey ? `${prefix}:${baseKey}` : null;
};

export const browserIncidentRelaySender: IncidentSender<BrowserDispatchedIncident, 'browser_relay'> = {
    sink: 'browser_relay',
    send: async (incident): Promise<IncidentSenderResult> => {
        if (typeof fetch !== 'function') {
            return {
                delivered: false,
                reason: 'unavailable',
            };
        }

        const response = await fetch(incident.relayPath ?? DEFAULT_BROWSER_INCIDENT_RELAY_PATH, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                title: incident.title ?? incident.message,
                message: incident.message,
                occurredAt: incident.occurredAt,
                severity: resolveIncidentSinkAlertSeverity({
                    classification: incident.classification ?? 'incident',
                    severity: incident.severity,
                }),
                dedupeKey: buildBrowserRelayDedupeKey(incident),
                metadata: pruneUndefined({
                    classification: incident.classification,
                    errorCode: incident.errorCode,
                    errorCategory: incident.errorCategory,
                    requestId: incident.requestId,
                    correlationId: incident.correlationId,
                    operation: incident.operation,
                    clientId: incident.clientId,
                    platform: incident.platform,
                    ...(incident.metadata ?? {}),
                }),
            }),
            keepalive: incident.keepalive ?? true,
        });

        if (!response.ok) {
            throw new Error(`Browser incident relay responded with HTTP ${response.status}`);
        }

        return {
            delivered: true,
            reason: 'delivered',
        };
    },
};
